<template>
    <v-dialog v-model="dialog" max-width="1000px">
      <v-card v-if="reply">
        <v-card-title class="primary white--text">
          Respuesta de {{ reply.student.name }} {{ reply.student.lastName }}
          <v-spacer></v-spacer>
          <v-btn class="mt-2 ml-3" color="red" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
  
        <v-card-subtitle class="py-2 d-flex justify-space-between align-center">
          <div>
            <v-chip color="primary" class="mr-2">
              Tiempo: {{ formatTime(reply.time) }}
            </v-chip>
            <v-chip :color="getCompletionColor()">
              {{ isCompleted() ? 'Completado' : 'Incompleto' }}
            </v-chip>
          </div>
        </v-card-subtitle>
  
        <v-tabs v-model="activeTab" grow>
          <v-tab>Datos Generales</v-tab>
          <v-tab>Habilidades Digitales</v-tab>
        </v-tabs>
  
        <v-tabs-items v-model="activeTab">
          <!-- Sección 1: Datos Generales -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-list>
                  <v-list-item v-for="question in questionsSection0" :key="question.id">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ question.text }}</v-list-item-title>
                      <v-divider class="my-2"></v-divider>
                      
                      <!-- Single Choice (Sexo) -->
                      <template v-if="question.type === 'single_choice'">
                        <v-list-item-subtitle>
                          <v-chip color="primary" outlined>
                            {{ getOptionText(question.id, getAnswerValue(question.id)) }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </template>
                      
                      <!-- Multiple Choice (Equipos) -->
                      <template v-if="question.type === 'multiple_choice'">
                        <v-list-item-subtitle>
                          <v-chip 
                            v-for="(optionId, index) in getAnswerValue(question.id)" 
                            :key="index"
                            color="primary"
                            outlined
                            class="mr-2 my-1"
                          >
                            {{ getOptionText(question.id, optionId) }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </template>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-tab-item>
          
          <!-- Sección 2: Habilidades Digitales -->
          <v-tab-item>
            <v-card flat>
              <v-card-text class="scrollable-content">
                <v-card v-for="(question, index) in questionsSection1" :key="question.id" class="mb-6">
                  <v-card-title class="white--text book-paragraph primary">
                    <v-icon left color="white">mdi-comment-question</v-icon>
                    {{ `Pregunta ${index + 1}: ${question.text}` }}
                  </v-card-title>
                  
                  <v-card-text>
                    <div class="options-container">
                      <v-radio-group :value="getAnswerValue(question.id)" readonly>
                        <v-radio
                          v-for="option in question.options"
                          :key="`option-${question.id}-${option.id}`"
                          :value="option.id"
                          color="primary"
                          readonly
                        >
                          <template v-slot:label>
                            <span :style="{
                              color: getAnswerValue(question.id) === option.id ? getSkillLevelTextColor(option.id) : 'inherit',
                              fontWeight: getAnswerValue(question.id) === option.id ? 'bold' : 'normal'
                            }">
                              {{ option.text }}
                            </span>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </div>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>

      </v-card>
    </v-dialog>
  </template>
  
  <script>
  export default {
    props: {
      modalOpen: {
        type: Boolean,
        default: false
      },
      reply: {
        type: Object,
        default: null
      },
      pollActivity: {
        type: Object,
        default: null
      },
      questions: {
        type: [Array, Object],
        required: true
      }
    },
    data() {
      return {
        dialog: false,
        activeTab: 0,
      };
    },
    computed: {
      questionsFlat() {
        // Si questions ya es un array plano, simplemente lo devolvemos
        if (!Array.isArray(this.questions[0])) {
          return this.questions;
        }
        
        // Si está dividido en secciones, lo aplanamos
        return [
          ...(this.questions[0] || []),
          ...(this.questions[1] || [])
        ];
      },
      questionsSection0() {
        // Obtener las preguntas de la sección 0 (Datos Generales)
        if (Array.isArray(this.questions[0])) {
          return this.questions[0];
        }
        
        // Si no está dividido en secciones, filtramos por sección
        return this.questions.filter(q => q.section === 'Datos Generales' || !q.section);
      },
      questionsSection1() {
        // Obtener las preguntas de la sección 1 (Habilidades Digitales)
        if (Array.isArray(this.questions[1])) {
          return this.questions[1];
        }
        
        // Si no está dividido en secciones, filtramos por sección
        return this.questions.filter(q => q.section === 'Habilidades Digitales');
      }
    },
    watch: {
      modalOpen(val) {
        this.dialog = val;
      },
      dialog(val) {
        if (!val) {
          this.$emit('update:modalOpen', false);
        }
      }
    },
    methods: {
      closeModal() {
        this.dialog = false;
      },
      getSkillLevelTextColor(optionId) {
        const colors = {
          '0': '#ff5252', // error - rojo
          '1': '#fb8c00', // warning - naranja
          '2': '#2196f3', // info - azul
          '3': '#4caf50'  // success - verde
        };
        
        return colors[optionId] || '#000000';
      },
      formatTime(seconds) {
        if (!seconds || seconds < 0) return 'N/A';

        const totalSeconds = Math.floor(seconds); // Redondear correctamente
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;

        return minutes > 0
        ? `${minutes}m${remainingSeconds}s`
        : `${remainingSeconds}s`;
      },
      getAnswerValue(questionId) {
        if (!this.reply || !this.reply.answer || !this.reply.answer[questionId]) {
          return null;
        }
        
        // Para selección única
        if (this.reply.answer[questionId].answerId !== undefined) {
          return this.reply.answer[questionId].answerId;
        }
        
        // Para texto abierto
        if (this.reply.answer[questionId].answerText !== undefined) {
          return this.reply.answer[questionId].answerText;
        }
        
        return null;
      },
      getOptionText(questionId, optionId) {
        if (!optionId) return "Sin respuesta";
        
        const question = this.questionsFlat.find(q => q.id === questionId);
        if (!question || !question.options) return "Opción no encontrada";
        
        const option = question.options.find(opt => opt.id === optionId);
        return option ? option.text : "Opción no encontrada";
      },
      isCompleted() {
        if (!this.reply || !this.reply.answer) return false;
        
        return this.questionsFlat.every(question => {
          const answer = this.reply.answer[question.id];
          if (!answer) return false;
          
          if (question.type === 'multiple_choice') {
            return answer.answerId && answer.answerId.length > 0;
          } else {
            return answer.answerId !== null && answer.answerId !== undefined;
          }
        });
      },
      getCompletionColor() {
        return this.isCompleted() ? 'success' : 'warning';
      }
    }
  };
  </script>